import * as documentActions from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.actions";
import * as documentHooks from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.hooks";
import { EDocumentCategory } from "@rentiohq/shared-frontend/dist/types/document.types";
import { formatLongDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { isNotFoundError } from "@rentiohq/shared-frontend/dist/utils/error.utils";
import { FileFieldInternal } from "@rentiohq/web-shared/dist/components";
import { useDispatch } from "react-redux";
import usePreference from "scenes/Settings/hooks/usePreference";
import { SharedSettingsProps } from "../Settings.types";

interface SettingsImageUploadProps extends SharedSettingsProps {
  title: string;
  tags?: string[];
  fileNamePrefix?: string;
}

// eslint-disable-next-line import/no-default-export
export default function SettingsImageUpload({
  title,
  preferenceKey,
  preferencePersistScope,
  fileNamePrefix = "Image",
  tags = [],
}: SettingsImageUploadProps) {
  const dispatch = useDispatch();

  const [image, saveImage, clearImage] = usePreference({
    preferenceKey,
    preferencePersistScope,
  });

  // The preference only stores a document id, so it can outlive the document it
  // points to. When that document is gone, fall back to an empty field so a new
  // image can be uploaded in its place instead of the field being stuck.
  const { fetchError } = documentHooks.useDetail({
    shouldRefetch: false,
    id: image,
  });

  const imageId = isNotFoundError(fetchError) ? undefined : image;

  const uploadImage = async (newValue: any) => {
    return new Promise<string>((resolve, reject) => {
      dispatch(
        documentActions.uploadDocumentStart.getAction({
          file: newValue?.[0],
          metaData: {
            filename: `${fileNamePrefix}(${formatLongDate(new Date())})`,
            categoryId: EDocumentCategory.Image,
            tags,
          },
          onSuccess: resolve,
          onFailure: reject,
        }),
      );
    });
  };

  return (
    <FileFieldInternal
      title={title}
      multiple={false}
      disabled={false}
      accept={"image/*"}
      formData={imageId ? [imageId] : []}
      hideInput={!!imageId}
      onChange={async (newValue: any[]) => {
        if (newValue.length === 0) {
          clearImage();
          return;
        }

        if (typeof newValue?.[0] !== "object") {
          return;
        }

        const documentId = await uploadImage(newValue);
        saveImage(documentId ?? null);
      }}
    />
  );
}
